import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "q-mr-sm" }

import { computed, ref, onMounted, watch } from "vue";
import { GitHubProjectSchema, MinimalProps } from "@/types";
import { gitHubApiStore, integrationsStore } from "@/hooks";
import {
  FlexBox,
  SelectInput,
  MultiselectInput,
  TextInput,
  IconButton,
  ExpansionItem,
} from "@/components/common";
import { GitHubAuthentication } from "@/components/integrations/authentication";


/**
 * Allows for selecting a GitHub repository.
 */
const __default__ = {
  name: "GitHubProjectInput",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    minimal: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const projects = ref<GitHubProjectSchema[]>([]);
const filePaths = ref<string[]>([]);

const organizationName = computed(
  () => integrationsStore.gitHubOrganization?.name
);

const projectName = computed(() => integrationsStore.gitHubProject?.name);

const branches = computed(
  () => integrationsStore.gitHubProject?.branches || []
);
const importBranch = computed({
  get: () => integrationsStore.gitHubConfig.branch,
  set: (branch) => (integrationsStore.gitHubConfig.branch = branch),
});

const includePatterns = computed({
  get: () => integrationsStore.gitHubConfig.include || [],
  set: (include) => (integrationsStore.gitHubConfig.include = include),
});
const excludePatterns = computed({
  get: () => integrationsStore.gitHubConfig.exclude || [],
  set: (exclude) => (integrationsStore.gitHubConfig.exclude = exclude),
});
const artifactType = computed({
  get: () => integrationsStore.gitHubConfig.artifactType,
  set: (artifactType) =>
    (integrationsStore.gitHubConfig.artifactType = artifactType),
});

/**
 * Opens the documentation for file patterns.
 */
function handleFilePatternInfo() {
  window.open(
    "https://docs.oracle.com/en/java/javase/16/docs/api/java.base/java/nio/file/FileSystem.html#getPathMatcher(java.lang.String)"
  );
}

/**
 * Loads a user's GitHub projects for a selected organization.
 */
function handleReload() {
  if (!integrationsStore.gitHubOrganization) return;

  integrationsStore.gitHubProject = undefined;

  projects.value = gitHubApiStore.projectList.filter(
    ({ owner }) => owner === organizationName.value
  );
}

onMounted(() => handleReload());

watch(
  () => organizationName.value,
  () => handleReload()
);

watch(
  () => integrationsStore.gitHubProject,
  () => {
    filePaths.value = [];
  }
);

watch(
  () => filePaths.value,
  (newPaths, oldPaths) => {
    const newGlobs = newPaths.map((path) =>
      path.endsWith("/") ? `${path}**` : `${path}/**`
    );
    const oldGlobs = oldPaths.map((path) =>
      path.endsWith("/") ? `${path}**` : `${path}/**`
    );

    const include = [
      ...(integrationsStore.gitHubConfig.include || []).filter(
        (pattern) => !oldGlobs.includes(pattern)
      ),
      ...newGlobs,
    ];

    // If the include array is empty, it will be removed from the config,
    // as an empty include means include no artifacts.
    if (include.length === 0) {
      integrationsStore.gitHubConfig.include = undefined;
    } else {
      integrationsStore.gitHubConfig.include = include;
    }
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (!_unref(integrationsStore).validGitHubCredentials)
      ? (_openBlock(), _createBlock(_unref(GitHubAuthentication), { key: 0 }))
      : (_openBlock(), _createBlock(_unref(SelectInput), {
          key: 1,
          modelValue: _unref(integrationsStore).gitHubOrganization,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(integrationsStore).gitHubOrganization) = $event)),
          label: "GitHub Organization",
          options: _unref(gitHubApiStore).organizationList,
          loading: _unref(gitHubApiStore).loading,
          hint: "Required. You can also type in a public organization and click 'Enter'.",
          class: "full-width q-mb-sm",
          "option-label": "name",
          "data-cy": "input-github-organization",
          "use-input": "",
          clearable: "",
          "new-value-mode": "add-unique",
          onNewValue: _cache[1] || (_cache[1] = 
        (val) =>
          (_unref(integrationsStore).gitHubOrganization = {
            name: val || '',
            id: val || '',
          })
      )
        }, null, 8, ["modelValue", "options", "loading"])),
    (!!_unref(integrationsStore).gitHubOrganization)
      ? (_openBlock(), _createBlock(_unref(SelectInput), {
          key: 2,
          modelValue: _unref(integrationsStore).gitHubProject,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(integrationsStore).gitHubProject) = $event)),
          label: "GitHub Repository",
          options: projects.value,
          hint: "Required. You can also type in a public repository and click 'Enter'.",
          class: "full-width q-mb-sm",
          "option-label": "name",
          "data-cy": "input-github-project",
          "use-input": "",
          clearable: "",
          "new-value-mode": "add-unique",
          onNewValue: _cache[3] || (_cache[3] = 
        (val) =>
          (_unref(integrationsStore).gitHubProject = {
            name: val || '',
            id: val || '',
            description: '',
            size: 0,
            owner: _unref(integrationsStore).gitHubOrganization?.name || '',
            branches: [],
            defaultBranch: '',
            creationDate: '',
          })
      )
        }, null, 8, ["modelValue", "options"]))
      : _createCommentVNode("", true),
    (!!projectName.value)
      ? (_openBlock(), _createBlock(_unref(FlexBox), {
          key: 3,
          column: "",
          t: "1"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(SelectInput), {
              modelValue: importBranch.value,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((importBranch).value = $event)),
              label: "Import Branch",
              options: branches.value,
              class: "full-width q-mb-sm",
              hint: "The branch to import files from, using the default branch if not specified.",
              "use-input": "",
              clearable: "",
              "new-value-mode": "add-unique"
            }, null, 8, ["modelValue", "options"]),
            _createVNode(_unref(MultiselectInput), {
              modelValue: filePaths.value,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((filePaths).value = $event)),
              label: "Import Path",
              options: [],
              "add-values": "",
              class: "full-width q-mb-sm",
              hint: "The file path(s) of the core code, such as 'src/'. Press enter to save."
            }, null, 8, ["modelValue"])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (!!_unref(integrationsStore).gitHubProject)
      ? (_openBlock(), _createBlock(_unref(ExpansionItem), {
          key: 4,
          label: "Advanced Configuration"
        }, {
          default: _withCtx(() => [
            (!!projectName.value)
              ? (_openBlock(), _createBlock(_unref(FlexBox), {
                  key: 0,
                  column: "",
                  t: "1"
                }, {
                  default: _withCtx(() => [
                    (!props.minimal)
                      ? (_openBlock(), _createBlock(_unref(TextInput), {
                          key: 0,
                          modelValue: artifactType.value,
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((artifactType).value = $event)),
                          label: "Artifact Type",
                          class: "full-width q-mb-sm",
                          hint: "A name for imported artifacts."
                        }, null, 8, ["modelValue"]))
                      : _createCommentVNode("", true),
                    _createVNode(_unref(FlexBox), {
                      y: "1",
                      align: "center"
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_1, [
                          _createVNode(_unref(MultiselectInput), {
                            modelValue: includePatterns.value,
                            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((includePatterns).value = $event)),
                            label: "Include Patterns",
                            options: [],
                            "add-values": "",
                            hint: "Press enter to save a regex file path pattern."
                          }, null, 8, ["modelValue"]),
                          _createVNode(_unref(MultiselectInput), {
                            modelValue: excludePatterns.value,
                            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((excludePatterns).value = $event)),
                            label: "Exclude Patterns",
                            options: [],
                            "add-values": "",
                            hint: "Press enter to save a regex file path pattern."
                          }, null, 8, ["modelValue"])
                        ]),
                        _createVNode(_unref(IconButton), {
                          tooltip: "Learn about file patterns",
                          icon: "info",
                          onClick: handleFilePatternInfo
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})